/**
 * formsApi.ts — thin client for /api/forms (Form Builder Phase 1b).
 *
 * Mirrors the per-namespace pattern of `customFieldsApi.ts` /
 * `todosApi.ts`. Lives outside `services/api.ts` (frozen) so this
 * graduation ships without an unlock for that file.
 */
import { api, API_BASE_URL } from '../../services/apiClient';
import type { PlacedBlock } from '../engine/formBuilderDesigns/_shared';

export interface FormSettings {
  form_name?: string;
  submit_button_label?: string;
  [k: string]: any;
}

export interface PublicFieldDef {
  id: number;
  name: string;
  label: string;
  field_type: string;
  options?: string[];
  is_required: boolean;
  /** Select / multi-select fields may allow a free-text "Other" choice. */
  allow_other?: boolean;
  /** Address fields: which components to collect + optional geo autocomplete. */
  address_components?: string[];
  address_autocomplete?: boolean;
}

export interface FormFull {
  id: number;
  name: string;
  slug: string;
  blocks: PlacedBlock[];
  settings: FormSettings;
  thank_you_content: string | null;
  redirect_form_id: number | null;
  is_published: boolean;
  created_by?: number | null;
  created_at: string;
  updated_at: string;
  /** Present on public (by-slug) responses; absent on owner GET. */
  field_defs?: PublicFieldDef[];
}

export interface SubmissionContact {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  company_name: string | null;
  title: string | null;
}

export interface FormSubmissionFull {
  id: number;
  form_id: number;
  submitted_at: string;
  answers: Record<string, any>;
  source_card_type: string | null;
  source_card_id: number | null;
  /** The CRM contact this submission created/updated (by work email), if any. */
  contact_id: number | null;
  contact: SubmissionContact | null;
}

/** Where a respondent's report email got to — mirrors the backend's
 *  assessmentReportDeliveryService.ReportDelivery. */
export type ReportDeliveryState =
  | 'report_opened'  // clicked through to the report — the surest sign
  | 'email_opened'   // the email was opened (an email app can do this on its own)
  | 'delivered'
  | 'complained'
  | 'bounced'
  | 'failed'         // the email service refused the send
  | 'no_record';

export interface ReportDelivery {
  submission_id: number;
  state: ReportDeliveryState;
  to: string | null;
  sends: number;
  sent_at: string | null;
  last_sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  complained_at: string | null;
  problem: string | null;
}

export interface CreateFormInput {
  name?: string;
  blocks?: PlacedBlock[];
  settings?: FormSettings;
  thank_you_content?: string | null;
}

export interface UpdateFormInput {
  name?: string;
  blocks?: PlacedBlock[];
  settings?: FormSettings;
  thank_you_content?: string | null;
  redirect_form_id?: number | null;
  slug?: string;
}

type SuccessEnvelope<T> = { success: true; data: T };
const PREFIX = '/forms';

export const formsApi = {
  list: (): Promise<SuccessEnvelope<FormFull[]>> =>
    api.get(PREFIX).then((r) => r.data),

  get: (id: number): Promise<SuccessEnvelope<FormFull>> =>
    api.get(`${PREFIX}/${id}`).then((r) => r.data),

  /** Public read by slug — only returns published forms. */
  getBySlug: (slug: string): Promise<SuccessEnvelope<FormFull>> =>
    api.get(`${PREFIX}/by-slug/${slug}`).then((r) => r.data),

  create: (input: CreateFormInput): Promise<SuccessEnvelope<FormFull>> =>
    api.post(PREFIX, input).then((r) => r.data),

  update: (id: number, input: UpdateFormInput): Promise<SuccessEnvelope<FormFull>> =>
    api.patch(`${PREFIX}/${id}`, input).then((r) => r.data),

  publish: (id: number, isPublished: boolean): Promise<SuccessEnvelope<FormFull>> =>
    api.post(`${PREFIX}/${id}/publish`, { is_published: isPublished }).then((r) => r.data),

  /** Archive / restore — hides the form from the list (settings.archived) without deleting it. */
  archive: (id: number, archived: boolean): Promise<SuccessEnvelope<FormFull>> =>
    api.post(`${PREFIX}/${id}/archive`, { archived }).then((r) => r.data),

  remove: (id: number): Promise<SuccessEnvelope<{ deleted: true }>> =>
    api.delete(`${PREFIX}/${id}`).then((r) => r.data),

  listSubmissions: (id: number): Promise<SuccessEnvelope<FormSubmissionFull[]>> =>
    api.get(`${PREFIX}/${id}/submissions`).then((r) => r.data),

  /** Every submission across every form (newest first) — the CD-22 global
   *  Submissions tab. Each row carries form_id; resolve the form name from the
   *  forms list. */
  listAllSubmissions: (): Promise<SuccessEnvelope<FormSubmissionFull[]>> =>
    api.get(`${PREFIX}/all-submissions`).then((r) => r.data),

  /** Per-form submission counts (total + last-7-days), keyed by form_id — the
   *  Forms list "Responses" column + the hub metric strip, in one call. */
  submissionCounts: (): Promise<SuccessEnvelope<Record<number, { total: number; last7d: number }>>> =>
    api.get(`${PREFIX}/submission-counts`).then((r) => r.data),

  /** Delete a single submission (+ its structured answers). For clearing tests. */
  deleteSubmission: (submissionId: number): Promise<SuccessEnvelope<{ deleted: true }>> =>
    api.delete(`${PREFIX}/submissions/${submissionId}`).then((r) => r.data),

  /** FR4: download responses as a real .xlsx (questions as columns, one sheet
   *  per form). Fetched as a blob so auth applies, then handed to the browser.
   *  `formId` scopes to one form; omit for every form that has submissions. */
  downloadSubmissionsXlsx: async (formId?: number | null, formName?: string): Promise<void> => {
    const qs = formId ? `?form_id=${formId}` : '';
    const res = await api.get(`${PREFIX}/submissions/export.xlsx${qs}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    const slug = (formName || 'form-submissions').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    a.href = url;
    a.download = `${slug || 'form-submissions'}-${stamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /** Public submit by slug. */
  submit: (
    slug: string,
    answers: Record<string, any>,
  ): Promise<SuccessEnvelope<{
    submission_id: number;
    form_id: number;
    submitted_at: string;
    redirect_form_id: number | null;
    redirect_form_slug: string | null;
    thank_you_content: string | null;
    /** Assessment/report forms: the respondent's report URL (immediate view). */
    report_url?: string | null;
  }>> =>
    api.post(`${PREFIX}/by-slug/${slug}/submit`, { answers }).then((r) => r.data),

  /** Unread 'form_submission' notification count for the current user — the
   *  paprika Forms rail badge (server count, refetched on load/navigation). */
  submissionsUnreadCount: (): Promise<SuccessEnvelope<{ count: number }>> =>
    api.get(`${PREFIX}/submissions/unread-count`).then((r) => r.data),

  /** Clear the current user's unread form-submission alerts (opened the surface). */
  markSubmissionsSeen: (): Promise<SuccessEnvelope<{ cleared: number }>> =>
    api.post(`${PREFIX}/submissions/mark-seen`).then((r) => r.data),

  /** Authed URL for a submission's scored report HTML (open in a new tab —
   *  the session cookie authorizes it). Part 2. */
  reportUrl: (submissionId: number): string =>
    `${API_BASE_URL}/forms/submissions/${submissionId}/report`,

  /** The public, shareable (tokened) report link — for the Share composer. */
  reportShareLink: (submissionId: number): Promise<SuccessEnvelope<{ url: string }>> =>
    api.get(`${PREFIX}/submissions/${submissionId}/report-link`).then((r) => r.data),

  /** Did each respondent get their report? Delivery status for every submission
   *  on an assessment form, keyed by submission id (read back from the email
   *  service's delivery events). */
  reportDeliveries: (): Promise<SuccessEnvelope<Record<string, ReportDelivery>>> =>
    api.get(`${PREFIX}/submissions/report-delivery`).then((r) => r.data),

  /** Email a submission's report to the respondent again. Rejects with the
   *  email service's refusal when the send doesn't go out. */
  resendReport: (submissionId: number): Promise<SuccessEnvelope<{ to: string }>> =>
    api.post(`${PREFIX}/submissions/${submissionId}/resend-report`).then((r) => r.data),

  /** Live SAMPLE report HTML (no DB record) for the Settings → Preview tab.
   *  Raw HTML string (rendered with this form's settings) to drop into an
   *  iframe srcDoc so the owner can stress-test the report + CTA wiring. */
  sampleReport: (id: number): Promise<string> =>
    api
      .get(`${PREFIX}/${id}/sample-report`, { responseType: 'text', transformResponse: (d) => d })
      .then((r) => r.data as string),
};
